import React from "react";

/**
 * The welcome screen's outline while a public form is still being fetched.
 *
 * One block, not a bar per line. A skeleton drawn line by line is a promise
 * about text nobody has read yet — how many lines the greeting takes, where it
 * breaks, how long the sentence under it runs — and every one of those is
 * wrong for at least one practitioner: a name that wraps, a brand with no
 * subtitle. When the read lands, each bar is replaced by something a different
 * size and the whole screen resettles. A single block promises only that
 * something of about this size is coming, which is the only thing actually
 * known, so what arrives lands inside the shape rather than knocking it about.
 *
 * The mark above it is the shell's own — see `PublicFormShell`'s `loading`.
 * Between them that is the whole of the page: the logo, and everything else.
 *
 * The welcome screen, not the form. Both forms open on a greeting, so a
 * skeleton shaped like a page of cards would be a picture of a screen nobody
 * is about to see — and on the enquiry form it used to draw grey cards on the
 * form's tinted background before the read landed on a white screen with a
 * centred greeting, so the background changed colour under it too.
 *
 * The height is the greeting's, near enough: a heading, a sentence, and a
 * button, with the air the design puts between them. Taller on a phone than on
 * a wide screen, which looks backwards written down and is right on the page —
 * the same words wrap to more lines in a narrower column. It is a placeholder
 * either way: the block is centred in the space the content will be centred
 * in, so what it is a few pixels out by is shared top and bottom rather than
 * shunting the page.
 *
 * Left against the gutter on a phone and centred from `sm:` up, which is where
 * both welcome screens put their own content.
 */
const PublicFormSkeleton: React.FC = () => (
  <div
    aria-hidden="true"
    className={
      "h-[320px] w-full animate-pulse rounded-2xl bg-[#EDF1F4] " +
      "sm:mx-auto sm:h-[300px] sm:max-w-[560px]"
    }
  />
);

export default PublicFormSkeleton;
